import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, Trash2, Upload, Plus, ImagePlus, Move } from "lucide-react";
import { toast } from "sonner";

/**
 * ResourceImageUploader — uploader réutilisable pour collaborations & événements.
 *
 * Props:
 *   resourceType: "collaborations" | "events"
 *   resourceId: string (UUID)
 *   coverImage: string | null   (storage_path)
 *   galleryImages: string[]     (storage_paths)
 *   posterImage?: string | null
 *   pastGallery?: string[]
 *   max: { gallery: number, past?: number }
 *   allowPoster?: boolean       (events only)
 *   allowPast?: boolean         (events only)
 *   onChange: (updates) => void
 */
export default function ResourceImageUploader({
  resourceType,
  resourceId,
  coverImage,
  galleryImages = [],
  posterImage = null,
  pastGallery = [],
  max = { gallery: 4 },
  allowPoster = false,
  allowPast = false,
  onChange,
}) {
  const galleryInput = useRef();
  const coverInput = useRef();
  const posterInput = useRef();
  const pastInput = useRef();
  const [uploading, setUploading] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const applyReorder = async (next) => {
    // Optimistic reorder — let parent refresh afterwards
    try {
      const { data } = await api.put(`/${resourceType}/${resourceId}/images/reorder`, {
        gallery_images: next,
      });
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de réorganisation");
    }
  };

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(idx));
    } catch (_) {}
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || idx === dragIdx) return;
    setDragOverIdx(idx);
  };
  const onDragLeave = () => setDragOverIdx(null);
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from === null || from === idx) return;
    const next = [...galleryImages];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    applyReorder(next);
  };
  const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const upload = async (kind, file) => {
    if (!file || !resourceId) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image trop lourde (15MB max)");
      return;
    }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post(`/${resourceType}/${resourceId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image ajoutée");
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(null); }
  };

  const remove = async (storage_path) => {
    try {
      const { data } = await api.delete(`/${resourceType}/${resourceId}/images`, {
        params: { storage_path },
      });
      toast.success("Image supprimée");
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const setAsCover = async (storage_path) => {
    if (resourceType !== "collaborations") return;
    try {
      const { data } = await api.put(`/${resourceType}/${resourceId}/images/reorder`, {
        gallery_images: galleryImages,
        cover_image: storage_path,
      });
      toast.success("Image mise en couverture");
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const move = async (idx, dir) => {
    const next = [...galleryImages];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    try {
      const { data } = await api.put(`/${resourceType}/${resourceId}/images/reorder`, {
        gallery_images: next,
      });
      onChange?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const canAddGallery = galleryImages.length < max.gallery;
  const canAddPast = !allowPast ? false : pastGallery.length < (max.past || 8);

  return (
    <div className="space-y-6" data-testid="resource-image-uploader">

      {/* COVER */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="caption-label text-[#A26F3E]">Image principale</p>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-[#EADDC9]"
            onClick={() => coverInput.current?.click()}
            disabled={uploading === "cover"}
            data-testid="upload-cover-btn"
          >
            {uploading === "cover" ? <Upload className="w-4 h-4 animate-pulse" /> : <><Plus className="w-4 h-4 mr-1" /> {coverImage ? "Remplacer" : "Ajouter"}</>}
          </Button>
          <input ref={coverInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("cover", e.target.files?.[0])} data-testid="cover-file-input" />
        </div>
        {coverImage ? (
          <div className="relative rounded-2xl overflow-hidden aspect-[16/10] bg-[#FAF3EA]">
            <img src={`${API}/files/${coverImage}`} alt="" className="w-full h-full object-cover" data-testid="cover-preview" />
            <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] tracking-widest uppercase">Couverture</span>
          </div>
        ) : (
          <button
            onClick={() => coverInput.current?.click()}
            className="w-full aspect-[16/10] rounded-2xl border-2 border-dashed border-[#EADDC9] bg-[#FAF3EA] flex flex-col items-center justify-center text-[#A26F3E] hover:bg-[#F0E0C7] transition"
            data-testid="cover-dropzone"
          >
            <ImagePlus className="w-8 h-8 mb-2" />
            <span className="text-sm">Cliquez pour ajouter une image principale</span>
            <span className="text-xs text-[#8C7D6B] mt-1">JPG / PNG / WebP — max 15 MB</span>
          </button>
        )}
      </div>

      {/* GALLERY */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="caption-label text-[#A26F3E]">
              {resourceType === "events" ? "Galerie de l'événement" : "Images secondaires"}
            </p>
            <p className="text-xs text-[#8C7D6B] mt-0.5">
              {galleryImages.length} / {max.gallery} images
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-[#EADDC9]"
            onClick={() => galleryInput.current?.click()}
            disabled={!canAddGallery || uploading === "gallery"}
            data-testid="upload-gallery-btn"
          >
            {uploading === "gallery" ? <Upload className="w-4 h-4 animate-pulse" /> : <><Plus className="w-4 h-4 mr-1" /> Ajouter</>}
          </Button>
          <input ref={galleryInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("gallery", e.target.files?.[0])} data-testid="gallery-file-input" />
        </div>
        {galleryImages.length === 0 ? (
          <div className="rounded-2xl bg-[#FAF3EA] p-6 text-center text-xs text-[#8C7D6B]">
            Aucune image secondaire. Ajoutez-en pour enrichir votre {resourceType === "events" ? "événement" : "collaboration"}.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {galleryImages.map((p, i) => {
              const isDragging = dragIdx === i;
              const isDropTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;
              return (
              <div
                key={p}
                draggable
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver(i)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(i)}
                onDragEnd={onDragEnd}
                className={`relative group aspect-square rounded-xl overflow-hidden bg-[#FAF3EA] cursor-grab active:cursor-grabbing transition-all ${
                  isDragging ? "opacity-40 scale-95" : ""
                } ${isDropTarget ? "ring-2 ring-[#C68E59] ring-offset-2 ring-offset-[#FFFBF5] scale-105" : ""}`}
                data-testid={`gallery-item-${i}`}
              >
                <img src={`${API}/files/${p}`} alt="" className="w-full h-full object-cover pointer-events-none" draggable="false" />
                {coverImage === p && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#C68E59] text-white text-[10px] tracking-widest uppercase pointer-events-none">
                    Couv.
                  </span>
                )}
                <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/55 backdrop-blur text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none font-mono">
                  {i + 1}
                </span>
                <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                  <span className="absolute bottom-1 inset-x-0 text-center text-[9px] tracking-widest uppercase text-white/75 pointer-events-none">
                    Glisser pour réordonner
                  </span>
                  {resourceType === "collaborations" && coverImage !== p && (
                    <button onClick={() => setAsCover(p)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center" title="Mettre en couverture" data-testid={`set-cover-${i}`}>
                      <Star className="w-3.5 h-3.5 text-[#C68E59]" />
                    </button>
                  )}
                  {i > 0 && (
                    <button onClick={() => move(i, -1)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center" title="Monter">
                      <Move className="w-3.5 h-3.5 text-[#1A1A1A] rotate-90" />
                    </button>
                  )}
                  <button onClick={() => remove(p)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center" title="Supprimer" data-testid={`delete-img-${i}`}>
                    <Trash2 className="w-3.5 h-3.5 text-[#A3483F]" />
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>

      {/* POSTER (events only) */}
      {allowPoster && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="caption-label text-[#A26F3E]">Affiche / bannière</p>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-[#EADDC9]"
              onClick={() => posterInput.current?.click()}
              disabled={uploading === "poster"}
              data-testid="upload-poster-btn"
            >
              {uploading === "poster" ? <Upload className="w-4 h-4 animate-pulse" /> : <><Plus className="w-4 h-4 mr-1" /> {posterImage ? "Remplacer" : "Ajouter"}</>}
            </Button>
            <input ref={posterInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("poster", e.target.files?.[0])} />
          </div>
          {posterImage ? (
            <div className="relative rounded-2xl overflow-hidden aspect-[3/4] max-w-xs bg-[#FAF3EA]">
              <img src={`${API}/files/${posterImage}`} alt="Affiche" className="w-full h-full object-cover" />
              <button onClick={() => remove(posterImage)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center">
                <Trash2 className="w-3.5 h-3.5 text-[#A3483F]" />
              </button>
            </div>
          ) : (
            <button onClick={() => posterInput.current?.click()} className="w-full aspect-[3/4] max-w-xs rounded-2xl border-2 border-dashed border-[#EADDC9] bg-[#FAF3EA] flex flex-col items-center justify-center text-[#A26F3E] hover:bg-[#F0E0C7] transition text-xs">
              <ImagePlus className="w-6 h-6 mb-1" />
              Ajouter une affiche
            </button>
          )}
        </div>
      )}

      {/* PAST EDITIONS (events only) */}
      {allowPast && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="caption-label text-[#A26F3E]">Éditions précédentes</p>
              <p className="text-xs text-[#8C7D6B] mt-0.5">{pastGallery.length} / {max.past || 8} photos</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-[#EADDC9]"
              onClick={() => pastInput.current?.click()}
              disabled={!canAddPast || uploading === "past"}
              data-testid="upload-past-btn"
            >
              {uploading === "past" ? <Upload className="w-4 h-4 animate-pulse" /> : <><Plus className="w-4 h-4 mr-1" /> Ajouter</>}
            </Button>
            <input ref={pastInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("past", e.target.files?.[0])} />
          </div>
          {pastGallery.length === 0 ? (
            <p className="text-xs text-[#8C7D6B] italic">Ajoutez des souvenirs des éditions précédentes pour donner envie.</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {pastGallery.map((p) => (
                <div key={p} className="relative group aspect-[3/4] rounded-lg overflow-hidden bg-[#FAF3EA]">
                  <img src={`${API}/files/${p}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => remove(p)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                    <Trash2 className="w-3 h-3 text-[#A3483F]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
